import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Input, InputNumber, Rate, message } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { envApiUrl } from '../../fetchHooks/envApiUrl';
import { handleCreateMyday } from '../../fetchHooks/handleCreateMyday';
import { handlePublishMyday } from '../../fetchHooks/handlePublishMyday';
import { handleUpdateMyday } from '../../fetchHooks/handleUpdateMyday';
import { useHealthmetrictypes } from '../../fetchHooks/useHealthmetrictypes';
import { useHealthmetrictypesWithMeasurements } from '../../fetchHooks/useHealthmetrictypesWithMeasurements';
import type { HealthMetricType } from '../../fetchHooks/useHealthmetrictypesWithMeasurements';
import type { MeasurementType, MydayType } from '../../fetchHooks/useMyday';
import type { UserType } from '../../fetchHooks/useUserProfile';

const { TextArea } = Input;
const WEIGHT_STEP_KG = 0.1;

interface QuickCreateMydayProps {
  userProfile?: UserType;
  onSuccess?: () => void;
}

// Custom hook to check if today has a published myday of type "measurement" with rateMental or ratePhysical measurements
const useHasTodayRatings = (rateMentalTypeId?: number, ratePhysicalTypeId?: number) => {
  const today = dayjs();
  const todayStr = today.format('YYYY-MM-DD');

  return useQuery({
    queryKey: ['hasTodayRatings', todayStr, rateMentalTypeId, ratePhysicalTypeId],
    queryFn: async () => {
      if (!rateMentalTypeId && !ratePhysicalTypeId) {
        console.log('[hasTodayRatings] No rate types provided');
        return false;
      }

      try {
        // Fetch mydays for today with type "measurement"
        const response = await axios.get(`${envApiUrl}/mydays/`, {
          params: {
            page: 1,
            limit: 100, // Increased limit to ensure we get all today's entries
            type: 'measurement',
          },
          withCredentials: true,
          xsrfCookieName: 'csrftoken',
          xsrfHeaderName: 'x-csrf-token',
        });

        const mydaysData = response.data?.data || [];

        if (!mydaysData || mydaysData.length === 0) {
          return false;
        }

        // Filter mydays for today that are published
        const todayMydays = mydaysData.filter((myday: MydayType) => {
          const mydayDate = dayjs(myday.date);
          const mydayDateStr = mydayDate.format('YYYY-MM-DD');
          return mydayDateStr === todayStr && myday.type === 'measurement' && myday.status === 'published';
        });

        if (todayMydays.length === 0) {
          return false;
        }

        // Fetch full details for today's published mydays to check measurements
        const checkPromises = todayMydays.map((myday: MydayType) =>
          axios
            .get(`${envApiUrl}/mydays/find_day`, {
              headers: {
                'Content-Type': 'application/json',
                id: myday.id,
              },
              withCredentials: true,
              xsrfCookieName: 'csrftoken',
              xsrfHeaderName: 'x-csrf-token',
            })
            .then((response) => response.data as MydayType)
            .catch(() => null),
        );

        const mydaysWithDetails = await Promise.all(checkPromises);

        // Check if any have rateMental or ratePhysical measurement
        return mydaysWithDetails.some((myday) => {
          if (!myday?.measurements) {
            return false;
          }

          return myday.measurements.some((measurement: MeasurementType) => {
            // Check both healthmetrictypeId (from /mydays/) and healthMetricType.id (from /mydays/find_day)
            const measurementTypeId = measurement.healthmetrictypeId || measurement.healthMetricType?.id;
            return measurementTypeId === rateMentalTypeId || measurementTypeId === ratePhysicalTypeId;
          });
        });
      } catch (error) {
        console.error('[hasTodayRatings] Error checking today ratings:', error);
        return false;
      }
    },
    enabled: Boolean(rateMentalTypeId || ratePhysicalTypeId),
    staleTime: 0, // Don't cache - always check fresh to ensure it updates immediately
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    gcTime: 0, // Don't keep in cache
  });
};

// Derive last weight value from types-with-measurements (most recent by myday date)
function useLastWeight(): number | null {
  const { data: typesWithMeasurements } = useHealthmetrictypesWithMeasurements();
  return useMemo(() => {
    const weightType = (typesWithMeasurements as HealthMetricType[] | undefined)?.find(
      (t: HealthMetricType) => t.name === 'weight',
    );
    const measurements = weightType?.measurements ?? [];
    if (measurements.length === 0) return null;
    const sorted = [...measurements].sort((a, b) => {
      const dateA = a.myday?.date ? new Date(a.myday.date).getTime() : 0;
      const dateB = b.myday?.date ? new Date(b.myday.date).getTime() : 0;
      return dateB - dateA;
    });
    const latest = sorted[0];
    return typeof latest?.value === 'number' ? latest.value : null;
  }, [typesWithMeasurements]);
}

export const QuickCreateMyday = ({ userProfile, onSuccess }: QuickCreateMydayProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedMentalRating, setSelectedMentalRating] = useState<number>(0);
  const [selectedPhysicalRating, setSelectedPhysicalRating] = useState<number>(0);
  const [weightValue, setWeightValue] = useState<number | null>(null);
  const [selectedLibido, setSelectedLibido] = useState<number>(0);
  const [selectedSleepScore, setSelectedSleepScore] = useState<number | null>(null);
  const [hrvValue, setHrvValue] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const lastWeight = useLastWeight();
  const { data: healthmetrictypes } = useHealthmetrictypes();

  // Find health metric types for quick log
  const types = (healthmetrictypes as HealthMetricType[] | undefined) ?? [];
  const rateMentalType = types.find((type: HealthMetricType) => type.name === 'rateMental');
  const ratePhysicalType = types.find((type: HealthMetricType) => type.name === 'ratePhysical');
  const weightType = types.find((type: HealthMetricType) => type.name === 'weight');
  const libidoType = types.find((type: HealthMetricType) => type.name === 'libido');
  const sleepScoreType = types.find((type: HealthMetricType) => type.name === 'sleepScore');
  const hrvType = types.find((type: HealthMetricType) => type.name === 'hrv');

  const rateMentalTypeId = rateMentalType?.id;
  const ratePhysicalTypeId = ratePhysicalType?.id;

  const hasAnyQuickLogValue =
    selectedMentalRating > 0 ||
    selectedPhysicalRating > 0 ||
    weightValue != null ||
    selectedLibido > 0 ||
    selectedSleepScore != null ||
    (hrvValue.trim() !== '' && !Number.isNaN(parseFloat(hrvValue)));

  // Check if user has already created ratings entry for today
  const { data: hasTodayEntry = false, isLoading: isLoadingCheck } = useHasTodayRatings(
    rateMentalTypeId,
    ratePhysicalTypeId,
  );

  const handleSubmit = async () => {
    const hasAnyType = rateMentalType || ratePhysicalType || weightType || libidoType || sleepScoreType || hrvType;
    if (!userProfile?.id || !hasAnyType) {
      message.error(t('myday.pleaseSelectRating', 'Please select a rating'));
      return;
    }

    if (!hasAnyQuickLogValue) {
      message.error(t('myday.pleaseSelectRating', 'Please select a rating'));
      return;
    }

    setLoading(true);
    setIsSubmitting(true); // Hide component immediately

    try {
      const today = dayjs().toDate();
      const mydayData = {
        userId: userProfile.id,
        year: today.getFullYear(),
        month: today.getMonth() + 1,
        day: today.getDate(),
        type: 'measurement',
        status: 'draft',
      };

      // Create draft myday
      const newMyday = await handleCreateMyday({
        data: mydayData,
        t,
        user: userProfile,
        queryClient,
        withConfirmation: false,
      });

      if (!newMyday?.id) {
        throw new Error('Failed to create myday');
      }

      // Build measurements only for values that are set
      const measurements = [];
      if (rateMentalType && selectedMentalRating > 0) {
        measurements.push({ healthmetrictypeId: rateMentalType.id, value: selectedMentalRating });
      }
      if (ratePhysicalType && selectedPhysicalRating > 0) {
        measurements.push({ healthmetrictypeId: ratePhysicalType.id, value: selectedPhysicalRating });
      }
      if (weightType && weightValue != null) {
        measurements.push({ healthmetrictypeId: weightType.id, value: weightValue });
      }
      if (libidoType && selectedLibido > 0) {
        measurements.push({ healthmetrictypeId: libidoType.id, value: selectedLibido });
      }
      if (sleepScoreType && selectedSleepScore != null) {
        measurements.push({ healthmetrictypeId: sleepScoreType.id, value: selectedSleepScore });
      }
      const hrvNum = parseFloat(hrvValue.trim());
      if (hrvType && hrvValue.trim() !== '' && !Number.isNaN(hrvNum)) {
        measurements.push({ healthmetrictypeId: hrvType.id, value: hrvNum });
      }

      const updateData: MydayType = {
        ...newMyday,
        ...mydayData,
        date: today,
        type: 'measurement',
        measurements,
        description: t('myday.quickLogDescription'),
        ...(note.trim() && { note: note.trim() }),
      };

      await handleUpdateMyday(updateData, newMyday, queryClient, async () => {
        // Publish the myday
        if (newMyday.id) {
          await handlePublishMyday(newMyday.id, async () => {
            setSelectedMentalRating(0);
            setSelectedPhysicalRating(0);
            setWeightValue(null);
            setSelectedLibido(0);
            setSelectedSleepScore(null);
            setHrvValue('');
            setNote('');
            // Invalidate queries to refresh data
            queryClient.invalidateQueries({ queryKey: ['useMydays'] });
            queryClient.invalidateQueries({ queryKey: ['useHealthmetrictypesWithMeasurements'] });
            // Invalidate and refetch the hasTodayRatings query immediately
            queryClient.invalidateQueries({
              queryKey: ['hasTodayRatings'],
              exact: false,
            });
            // Wait a bit for the backend to update, then refetch
            setTimeout(() => {
              queryClient.refetchQueries({
                queryKey: ['hasTodayRatings'],
                exact: false,
              });
            }, 500);
            onSuccess?.();
          });
        }
      });
    } catch (error) {
      console.error('Error creating quick myday:', error);
      message.error(t('myday.quickCreateFailed', 'Failed to log ratings. Please try again.'));
      setIsSubmitting(false); // Show component again if submission failed
    } finally {
      setLoading(false);
    }
  };

  const hasAnyQuickLogType =
    rateMentalType || ratePhysicalType || weightType || libidoType || sleepScoreType || hrvType;

  // Don't show if user has already created an entry for today, or if still checking, or if no quick log type exists, or if currently submitting
  if (hasTodayEntry || isLoadingCheck || !hasAnyQuickLogType || isSubmitting) {
    return null;
  }

  return (
    <Card
      style={{
        marginBottom: '1rem',
        backgroundColor: '#f0f8ff',
        border: '1px solid #1890ff',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div>
          <h3 style={{ margin: 0, marginBottom: '0.5rem' }}>
            {t('myday.quickLogRatings', 'Quick Log: How are you feeling?')}
          </h3>
        </div>

        {/* Mental State Rating */}
        {rateMentalType && (
          <div>
            <p style={{ margin: 0, marginBottom: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
              {t('myday.mentalState', 'Mental State')}:
            </p>
            <div>
              <Rate
                count={10}
                value={selectedMentalRating}
                onChange={setSelectedMentalRating}
                style={{ fontSize: '24px' }}
              />
              {selectedMentalRating > 0 && (
                <span style={{ marginLeft: '1rem', fontWeight: 'bold' }}>{selectedMentalRating}/10</span>
              )}
            </div>
          </div>
        )}

        {/* Physical State Rating */}
        {ratePhysicalType && (
          <div>
            <p style={{ margin: 0, marginBottom: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
              {t('myday.physicalState', 'Physical State')}:
            </p>
            <div>
              <Rate
                count={10}
                value={selectedPhysicalRating}
                onChange={setSelectedPhysicalRating}
                style={{ fontSize: '24px' }}
              />
              {selectedPhysicalRating > 0 && (
                <span style={{ marginLeft: '1rem', fontWeight: 'bold' }}>{selectedPhysicalRating}/10</span>
              )}
            </div>
          </div>
        )}

        {/* Weight (kg) – previous as suggestion, +/- 0.1 kg */}
        {weightType && (
          <div>
            <p style={{ margin: 0, marginBottom: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
              {t('myday.weight', 'Weight')}
              {lastWeight != null && (
                <span style={{ marginLeft: '0.5rem', fontWeight: 'normal', color: '#888' }}>
                  ({t('myday.previousWeight', 'Previous weight')}: {Number(lastWeight).toFixed(1)} kg)
                </span>
              )}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <Button
                onClick={() =>
                  setWeightValue((v) => {
                    const next = v ?? lastWeight ?? 0;
                    return Math.max(0, Math.round((next - WEIGHT_STEP_KG) * 10) / 10);
                  })
                }
              >
                −
              </Button>
              <InputNumber
                min={0}
                step={WEIGHT_STEP_KG}
                value={weightValue ?? undefined}
                placeholder={lastWeight != null ? String(lastWeight.toFixed(1)) : undefined}
                onChange={(val) => setWeightValue(val != null ? Number(val) : null)}
                style={{ width: 100 }}
                addonAfter="kg"
              />
              <Button
                onClick={() =>
                  setWeightValue((v) => {
                    const next = v ?? lastWeight ?? 0;
                    return Math.round((next + WEIGHT_STEP_KG) * 10) / 10;
                  })
                }
              >
                +
              </Button>
            </div>
          </div>
        )}

        {/* Libido (0–19) – same visual as Mental State (stars) */}
        {libidoType && (
          <div>
            <p style={{ margin: 0, marginBottom: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
              {t('myday.libido', 'Libido')}:
            </p>
            <div>
              <Rate count={10} value={selectedLibido} onChange={setSelectedLibido} style={{ fontSize: '24px' }} />
              {selectedLibido > 0 && (
                <span style={{ marginLeft: '1rem', fontWeight: 'bold' }}>{selectedLibido}/19</span>
              )}
            </div>
          </div>
        )}

        {/* Sleep score (0–100 %) */}
        {sleepScoreType && (
          <div>
            <p style={{ margin: 0, marginBottom: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
              {t('myday.sleepScore', 'Sleep score')} (0–100 %):
            </p>
            <InputNumber
              min={0}
              max={100}
              value={selectedSleepScore ?? undefined}
              onChange={(val) => setSelectedSleepScore(val != null ? val : null)}
              placeholder="—"
              style={{ width: 80 }}
              addonAfter="%"
            />
          </div>
        )}

        {/* HRV (ms) */}
        {hrvType && (
          <div>
            <p style={{ margin: 0, marginBottom: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
              {t('myday.hrv', 'HRV')} (ms):
            </p>
            <Input
              type="number"
              min={0}
              step={1}
              value={hrvValue}
              onChange={(e) => setHrvValue(e.target.value)}
              placeholder="—"
              style={{ width: 100 }}
            />
          </div>
        )}

        <div>
          <TextArea
            placeholder={t('myday.optionalNote', 'Optional note...')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            maxLength={500}
            showCount
          />
        </div>

        <div>
          <Button type="primary" onClick={handleSubmit} loading={loading} disabled={!hasAnyQuickLogValue} block>
            {t('myday.logRatings', 'Log Ratings')}
          </Button>
        </div>
      </div>
    </Card>
  );
};
